/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import type { $StoreState } from '../../types/StoreState';
import type { $Configuration } from 'CoreTypes/Configuration';
import type { $WidgetSettings } from '../../types/WidgetSettings';
import type { $WidgetEnvConfiguration } from '../../types';
import type { $Suggestion } from 'CoreTypes/AutoCompleteSuggestions';
import Keys from '../../constants/WidgetFieldsKeys';
import Measure from 'react-measure';
import DealersSearch from 'CoreComponents/DealersSearch/DealersSearch';
import FieldKeysGeneric from 'CoreConstants/FieldKeysGeneric';
import Globals from 'CoreConstants/Globals';
import Themes from 'CoreConstants/Themes';
import UnsupportedBrowser from 'CoreComponents/UnsupportedBrowser/UnsupportedBrowser';
import WidgetSearchResults from '../SearchResults/SearchResults';
import DMSConsumer from 'CoreConsumers/DMSConsumer';
import { getMediaQuery } from 'CoreConstants/WorkbenchBreakpoints';
import { getConfigurationTranslationValue, getConfigurationMarketProperty } from 'CoreUtils/WidgetUtils';
import { getWidgetFieldValueByKey } from 'CoreUtils/WidgetFieldUtils';
import { findDealers } from '../../store/search';
import { setCurrentViewMode } from '../../store/viewMode';
import { showWidgetField } from 'CoreUtils/WidgetFieldUtils';
import { dispatchEvent } from 'CoreUtils/WidgetUtils';
import { getCurrentPosition } from 'CoreStore/geolocation';
import { resetAllFilters } from 'CoreStore/filters';
import { isUserAgentIE } from 'CoreUtils/WidgetUtils';
import { getEnvConfiguration } from '../../environments';
import { getDMSEndpointURL } from 'CoreUtils/EndpointUtils';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';
/* import ViewMode from '../SearchResults/SearchResultsViewMode'; */

type $Props = {
  widgetHeadline: string,
  submitLabel: string,
  tabLabelLocation: string,
  tabLabelName: string,
  inputPlaceholderLocation: string,
  inputPlaceholderName: string,
  locateMeLabel: string,
  noSuggestionsLabel: string,
  showWidgetHeadline: boolean,
  languageRtl: boolean,
  configuration: $Configuration,
  widgetSettings: $WidgetSettings,
  initialViewMode: string,
  unsupportedBrowserWarning: string,
  findDealers: Function,
  getCurrentPosition: Function,
  setCurrentViewMode: Function,
  resetAllFilters: Function,
  suggestion?: $Suggestion
};

type $State = {
  widgetWidth: number,
  isSearchByLocationActive: boolean,
  searchLocation: string,
  searchName: string
};

class WidgetSearch extends Component<$Props, $State> {

  state = {
    widgetWidth: -1,
    isSearchByLocationActive: true,
    searchLocation: '',
    searchName: ''
  };

  envConfig: $WidgetEnvConfiguration = getEnvConfiguration(this.props.widgetSettings.env);
  dms = new DMSConsumer(getDMSEndpointURL(this.props.widgetSettings, this.envConfig));

  handleLocateMeButtonClick = async () => {
    await this.props.getCurrentPosition();
    this.props.resetAllFilters(); /* DL-1442 */
    this.props.findDealers({
      spellCheck: false,
      forceGeolocation: true
    });
  };

  handleSearch = (isSearchByLocationActive: boolean, searchLocation: string,
    searchName: string, suggestionClicked: $Suggestion) => {
    /* Disabled due to DL-1431 */
    /*
    if (this.props.initialViewMode === ViewMode.COMBO_MODE_MAP.key) {
      this.props.setCurrentViewMode(ViewMode.MAP_MODE.key);
    } else if (this.props.initialViewMode === ViewMode.COMBO_MODE_LIST.key) {
      this.props.setCurrentViewMode(ViewMode.LIST_MODE.key);
    }
    */

    this.setState({
      isSearchByLocationActive,
      searchLocation,
      searchName
    }, () => {
      this.props.resetAllFilters(); /* DL-1442 */
      this.props.findDealers({
        isSearchByLocationActive,
        searchLocationString: searchLocation,
        searchNameString: searchName,
        spellCheck: true,
        suggestion: suggestionClicked
      });
    });
  };

  handleFilteringChange = () => {
    this.props.findDealers({
      isSearchByLocationActive: this.state.isSearchByLocationActive,
      searchLocationString: this.state.searchLocation,
      searchNameString: this.state.searchName,
      spellCheck: true,
      forceGeolocation: true,
      suggestion: this.props.suggestion
    });
  };

  onWidgetResize = (contentRect: Object) => {
    if (contentRect && contentRect.bounds && contentRect.bounds.width) {
      this.setState({
        widgetWidth: contentRect.bounds.width
      });
    }
    dispatchEvent('resize');
  };

  getWrapperClassName = (mq: string, theme: string, rtl: ?boolean) => {
    const cn: Array<string> = [ 'dl-dsw' ];
    cn.push(`dl-${mq.toLowerCase()}`);
    cn.push(`dl-theme-${theme}`);
    cn.push(`${rtl ? 'dl-rtl' : 'dl-ltr'}`);
    return cn.join(' ');
  };

  render() {
    const { theme, market } = this.props.widgetSettings;
    const cMediaQuery = getMediaQuery(this.state.widgetWidth);
    const rtl = this.props.languageRtl;
    const wrapperBaseClass = this.getWrapperClassName(
      cMediaQuery,
      theme || Themes.THEME_LIGHT,
      rtl
    );

    if (isUserAgentIE(window.navigator.userAgent) === true) {
      return (
        <UnsupportedBrowser
          message={ this.props.unsupportedBrowserWarning }
          modifierClass="dl-dsw"
        />
      );
    }
    else return (
      <Measure bounds onResize={this.onWidgetResize}>
        {({ measureRef }) =>
        <div ref={measureRef} className={ wrapperBaseClass }>
          { this.props.showWidgetHeadline &&
            <h1 className="wb-e-hdl-1 dl-dsw-hdl">{ this.props.widgetHeadline }</h1>
          }
          <DealersSearch
            mq={ cMediaQuery }
            locateMeLabel={ this.props.locateMeLabel }
            noSuggestionsLabel= {this.props.noSuggestionsLabel }
            onLocateMeButtonClick={ this.handleLocateMeButtonClick }
            tabLabelLocation={ this.props.tabLabelLocation }
            tabLabelName={ this.props.tabLabelName }
            searchFieldPlaceholderLocation={ this.props.inputPlaceholderLocation }
            searchFieldPlaceholderName={ this.props.inputPlaceholderName }
            submitButtonLabel={ this.props.submitLabel }
            onSubmit={ this.handleSearch }
            getAddressSuggestions={ this.dms.getAddressSuggestions }
            marketCode={ market }
            autoSuggestionEnabled={ !getConfigurationMarketProperty(this.props.configuration, market || '',
              TemplateMarketPropertyKeys.GEN_DISABLE_AUTOSUGGEST) }
            showCountryInSuggestions={ !!getConfigurationMarketProperty(this.props.configuration, market || '',
              TemplateMarketPropertyKeys.AUTOSUGGEST_HIDE_COUNTRY) }
          />
          {/* TODO: DL-1688 - Remove element after validation! It's included here in a non-evasive way */}
          <wb-icon name="chevron-right" style={{ display: 'none'}} />
          <WidgetSearchResults onFilteringChange={ this.handleFilteringChange } />
        </div>
        }
      </Measure>
    );
  }
}

const mapStateToProps = (state: $StoreState) => ({
  widgetHeadline: getWidgetFieldValueByKey(Keys.HEADLINE_WIDGET, state.dsw),
  showWidgetHeadline: showWidgetField(state.dsw.settings, Keys.HEADLINE_WIDGET),
  submitLabel: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_SUBMIT_BUTTON_LABEL, state.dsw),
  tabLabelLocation: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_TAB_LABEL_LOCATION, state.dsw),
  tabLabelName: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_TAB_LABEL_NAME, state.dsw),
  inputPlaceholderLocation: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_FIELD_PLACEHOLDER_LOCATION, state.dsw),
  inputPlaceholderName: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_FIELD_PLACEHOLDER_NAME, state.dsw),
  locateMeLabel: getWidgetFieldValueByKey(Keys.BUTTON_LOCATE_ME_TOOLTIP, state.dsw),
  noSuggestionsLabel: getWidgetFieldValueByKey(Keys.DEALERS_SEARCH_FIELD_NO_SUGGESTIONS, state.dsw) ||
    Globals.SEARCH_NO_SUGGESTIONS,
  languageRtl: !!(state.dsw.configuration && state.dsw.configuration.languages &&
    state.dsw.configuration.languages[0] && state.dsw.configuration.languages[0].rtl),
  configuration: state.dsw.configuration,
  widgetSettings: state.dsw.settings,
  initialViewMode: state.dsw.viewMode.initial,
  suggestion: state.dsw.search.suggestion || null,
  unsupportedBrowserWarning: getConfigurationTranslationValue(
    state.dsw.translations || [],
    FieldKeysGeneric.UNSUPPORTED_BROWSER_WARNING
  )
});

const mapDispatchToProps: Object = {
  findDealers,
  getCurrentPosition,
  setCurrentViewMode,
  resetAllFilters
};

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSearch);
